import React, { useState, useEffect } from "react";
import "./LandingPage.css";
import { useNavigate } from "react-router-dom";
import { useContent } from "./ContentContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// Import local images as fallbacks
import freightForwarding from "./public/images/freightForwarding.jpg";
import Aviation from "./public/images/logisticsImage/cover.jpg";
import OverseasEmployment from "./public/images/OverseasEmployment.jpg";
import traveling from "./public/images/logisticsImage/traveling.jpg";
import FoodProcessing from "./public/images/fish.jpg";
import Education from "./public/images/education_image.jpg";

const imageMap = {
  "/images/freightForwarding.jpg": freightForwarding,
  "/images/logisticsImage/cover.jpg": Aviation,
  "/images/OverseasEmployment.jpg": OverseasEmployment,
  "/images/logisticsImage/traveling.jpg": traveling,
  "/images/fish.jpg": FoodProcessing,
  "/images/education_image.jpg": Education
};

export default function LandingPage() {
  const { content, loading } = useContent();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Swipe Handlers
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  const sections = content?.landingPage?.sections || [];

  useEffect(() => {
    if (!isMobile || sections.length === 0) return;
    const timer = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(timer);
  }, [currentIndex, sections.length, isMobile]);

  // Preload images to prevent lag on mobile
  useEffect(() => {
    sections.forEach((sec) => {
      const img = new Image();
      img.src = getImageUrl(sec.img);
    });
  }, [sections]);

  if (loading || !content || sections.length === 0) {
    return (
      <div className="landing-container loading-state">
        <div className="loader-box">
          <span>Al Rahim Group...</span>
        </div>
      </div>
    );
  }

  const getImageUrl = (img) => {
    if (img.startsWith('http')) return img;
    return imageMap[img] || img;
  };

  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % sections.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + sections.length) % sections.length);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
      transition: { duration: 0.5 }
    })
  };

  return (
    <div className="landing-container">
      {/* Desktop Grid View - Restored */}
      <div className="desktop-view right-panel">
        {sections.map((sec, index) => (
          <div
            key={index}
            className={`section ${hoverIndex === index ? "active" : ""}`}
            style={{ backgroundImage: `url(${getImageUrl(sec.img)})` }}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            {hoverIndex === index ? (
              <div className="go-button" onClick={() => navigate(sec.url)}>GO</div>
            ) : (
              <div className="plus-icon">+</div>
            )}

            <div className={hoverIndex === index ? "text-heading1" : "text-heading"}
              dangerouslySetInnerHTML={{ __html: sec.title }}>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Slider View */}
      <div
        className="mobile-view slider-wrapper"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="slide"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.4)), url(${getImageUrl(sections[currentIndex].img)})`
            }}
          >
            <div className="slide-content">
              <div
                className="slide-title"
                dangerouslySetInnerHTML={{ __html: sections[currentIndex].title }}
              />
              <button
                className="slide-button"
                onClick={() => navigate(sections[currentIndex].url)}
              >
                GO
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="slider-nav">
          <button className="nav-btn prev" onClick={prevSlide}><FaChevronLeft /></button>
          <button className="nav-btn next" onClick={nextSlide}><FaChevronRight /></button>
        </div>

        <div className="slider-dots">
          {sections.map((_, idx) => (
            <button
              key={idx}
              className={`dot ${idx === currentIndex ? "active" : ""}`}
              onClick={() => {
                setDirection(idx > currentIndex ? 1 : -1);
                setCurrentIndex(idx);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
